import type { FormEvent } from "react";
import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../AuthProvider";
import PageTemplate from "../components/PageTemplate";
import SlidePageTransition from "../components/transitions/SlidePageTransition";
import Modal from "../components/Modal";
import ParentForm from "./parents/components/ParentForm";
import { createParent } from "./parents/api";
import type { AppError } from "./parents/types";
import { useParentForm } from "./parents/hooks/useParentForm";
import {
  hasParentFormErrors,
  mapFormToCreatePayload,
  validateParentForm,
} from "./parents/utils/parentFormMapping";
import { FaEnvelope, FaPhone, FaSave, FaTimes, FaUser, FaUserPlus } from "react-icons/fa";
import { navigateParentFlow, parentFlowTransitionState, parentOverviewPath } from "./parents/navigation";

import BackButton, { useLeaveForm } from "../components/navigation/BackButton";
interface ParentNewModal {
  show: boolean;
  type: string;
  title: string;
  message: string;
  navigateOnClose?: string;
}

const ParentNew = () => {
  const { keycloak } = useContext(AuthContext);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const leaveForm = useLeaveForm();
  const { values, errors, setErrors, updateField } = useParentForm();
  const [saving, setSaving] = useState(false);
  const [modal, setModal] = useState<ParentNewModal>({
    show: false,
    type: "",
    title: "",
    message: "",
  });
  const previewName = `${values.firstName || ""} ${values.lastName || ""}`.trim();
  const previewInitials =
    previewName
      .split(/\s+/)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase())
      .slice(0, 2)
      .join("") || "?";
  const formId = "parent-new-form";

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextErrors = validateParentForm(values);
    setErrors(nextErrors);
    if (hasParentFormErrors(nextErrors)) {
      setModal({
        show: true,
        type: "error",
        title: t("common.validation_error", { defaultValue: "Validation Error" }),
        message: t("family.parents.common.validation_required", { defaultValue: "Fill in all required fields." }),
      });
      return;
    }

    try {
      setSaving(true);
      if (!keycloak?.token) throw new Error("No auth");
      await createParent(mapFormToCreatePayload(values));
      setModal({
        show: true,
        type: "success",
        title: t("common.modal.confirm"),
        message: t("family.parents.common.success_message"),
        navigateOnClose: parentOverviewPath,
      });
    } catch (error) {
      const ignoredError = error as AppError;
      setModal({
        show: true,
        type: "error",
        title: t("common.modal.confirm"),
        message: ignoredError.message ? t("family.parents.common.create_failed") : t("family.parents.common.create_failed"),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageTemplate title={null} wrapperClassName="max-w-7xl mx-auto">
      <SlidePageTransition className="mx-auto rounded-[30px] border border-slate-200/80 bg-white/95 p-5 text-slate-950 shadow-[0_28px_90px_rgba(15,23,42,0.12)] backdrop-blur dark:border-white/10 dark:bg-slate-950/80 dark:text-white sm:p-6">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <BackButton fallback={parentOverviewPath} />
          <div className="flex flex-wrap items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => navigateParentFlow(navigate, parentOverviewPath, "back")}
              className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:border-indigo-200 hover:text-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 motion-reduce:hover:translate-y-0 dark:border-white/10 dark:bg-white/10 dark:text-slate-100 dark:hover:text-indigo-200"
            >
              <FaTimes aria-hidden="true" />
              {t("common.cancel")}
            </button>
            <button
              type="submit"
              form={formId}
              className="lumen-glow-sheen inline-flex items-center justify-center gap-2 rounded-full border border-indigo-200/70 bg-indigo-50/80 px-6 py-2.5 text-sm font-semibold text-indigo-700 shadow-sm transition hover:-translate-y-0.5 hover:text-indigo-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-60 motion-reduce:hover:translate-y-0 dark:border-indigo-500/50 dark:bg-indigo-950/40 dark:text-indigo-200 dark:hover:text-white"
              disabled={saving}
            >
              <FaSave aria-hidden="true" />
              {saving ? t("common.loading") : t("family.parents.new.create")}
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-6">
          <header className="relative overflow-hidden rounded-[24px] border border-indigo-100/80 bg-gradient-to-r from-indigo-50/80 via-white to-sky-50/70 p-5 shadow-[0_18px_45px_rgba(67,56,202,0.12)] dark:border-indigo-400/25 dark:from-indigo-950/35 dark:via-slate-950 dark:to-sky-950/20 sm:p-6">
            <div className="pointer-events-none absolute inset-y-0 right-0 hidden w-[42%] opacity-75 lg:block">
              <div className="absolute bottom-4 right-7 h-20 w-48 rounded-t-full bg-sky-100/75 dark:bg-sky-300/10" />
              <div className="absolute bottom-4 right-24 h-28 w-40 rounded-t-full bg-indigo-100/70 dark:bg-indigo-300/10" />
              <div className="absolute bottom-7 right-12 h-24 w-2 rounded-full bg-indigo-200/80 dark:bg-indigo-200/25" />
              <div className="absolute bottom-8 right-7 h-16 w-2 rounded-full bg-sky-200/80 dark:bg-sky-200/20" />
              <div className="absolute right-28 top-10 h-4 w-16 rounded-full bg-white/80 dark:bg-white/10" />
              <div className="absolute bottom-8 right-44 grid grid-cols-5 gap-2 opacity-55">
                {Array.from({ length: 25 }).map((_, index) => (
                  <span
                    key={index}
                    className="h-1.5 w-1.5 rounded-full bg-indigo-300/70 dark:bg-indigo-200/30"
                  />
                ))}
              </div>
            </div>

            <div className="relative flex flex-col gap-5 md:flex-row md:items-center">
              <div className="relative flex h-28 w-28 shrink-0 items-center justify-center rounded-full border-[5px] border-white bg-indigo-50 text-3xl font-bold uppercase text-indigo-700 shadow-xl shadow-indigo-900/10 dark:border-white/20 dark:bg-indigo-400/15 dark:text-indigo-100 sm:h-32 sm:w-32">
                {previewName ? previewInitials : <FaUserPlus aria-hidden="true" />}
              </div>
              <div className="min-w-0">
                <h1 className="min-w-0 break-words text-3xl font-bold text-slate-950 dark:text-white sm:text-4xl">
                  {previewName || t("family.parents.new.title")}
                </h1>
                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <span className="inline-flex items-center rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-xs font-bold text-sky-800 dark:border-sky-400/40 dark:bg-sky-400/15 dark:text-sky-100">
                    {t("family.parents.new.subtitle", { defaultValue: "Add a parent" })}
                  </span>
                  <span className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 dark:text-slate-300">
                    <FaUser aria-hidden="true" className="text-indigo-600 dark:text-indigo-300" />
                    {t("family.parents.details.primary_contact", { defaultValue: "Primary contact" })}
                  </span>
                </div>
                <div className="mt-4 flex flex-wrap gap-3 text-sm font-medium text-slate-600 dark:text-slate-300">
                  <span className="inline-flex items-center gap-2">
                    <FaEnvelope aria-hidden="true" className="text-indigo-600 dark:text-indigo-300" />
                    {values.email || t("common.not_available", { defaultValue: "Not available" })}
                  </span>
                  <span className="inline-flex items-center gap-2">
                    <FaPhone aria-hidden="true" className="text-indigo-600 dark:text-indigo-300" />
                    {values.phone || t("common.not_available", { defaultValue: "Not available" })}
                  </span>
                </div>
              </div>
            </div>
          </header>

          <ParentForm
            formId={formId}
            hideActions
            mode="create"
            values={values}
            errors={errors}
            saving={saving}
            onChange={updateField}
            onSubmit={handleSubmit}
            onCancel={() => navigateParentFlow(navigate, parentOverviewPath, "back")}
            t={t}
          />
        </div>
      </SlidePageTransition>

      <Modal
        show={modal.show}
        type={modal.type}
        title={modal.title}
        message={modal.message}
        onClose={() => {
          const target = modal.navigateOnClose;
          setModal({ show: false, type: "", title: "", message: "" });
          if (target) leaveForm(target, { state: parentFlowTransitionState("back") });
        }}
      />
    </PageTemplate>
  );
};

export default ParentNew;
